import { useMutation } from '@apollo/client';
import { ArrowBack, Check, CheckCircle, Close } from '@material-ui/icons';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  CreateHuddleUpMutation,
  CreateHuddleUpMutationVariables,
  HuddleUpStatus,
  HuddleUpSubscriptionType,
  UpdateHuddleUpMutation,
  UpdateHuddleUpMutationVariables,
  UpdateProposalFeesMutation,
  UpdateProposalFeesMutationVariables,
} from 'lib/generated/graphql';
import { CREATE_HUDDLE_UP, UPDATE_FEES_HUDDLEUP_MEMBER, UPDATE_HUDDLE_UP } from 'lib/mutations';
import { GET_PROPOSAL_QUERY, GET_VIEWER } from 'lib/queries';
import Tick from 'public/images/icons/tick.svg';
import { useState } from 'react';
import { Button, Divider, Modal } from 'src/shared-components/';
import useAuth from 'src/shared-components/Auth';
import LoadingOverlay from 'src/shared-components/Loading/LoadingOverlay';

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY as string);

const HuddleUpPayment = ({ modalRef, saving, profilePic }: { modalRef: any; saving?: number; profilePic?: string }) => {
  const { user, refetch } = useAuth();
  const [loading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [step, setStep] = useState(0);
  const [options, setOptions] = useState({ clientSecret: '', loader: 'always' });
  const [huddleUpId, setHuddleUpId] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState(HuddleUpSubscriptionType.Annual);
  const [createHuddleUp] = useMutation<CreateHuddleUpMutation, CreateHuddleUpMutationVariables>(CREATE_HUDDLE_UP);
  const [updateHuddleUp] = useMutation<UpdateHuddleUpMutation, UpdateHuddleUpMutationVariables>(UPDATE_HUDDLE_UP, {
    refetchQueries: [GET_PROPOSAL_QUERY, GET_VIEWER],
  });

  const [updateFees] = useMutation<UpdateProposalFeesMutation, UpdateProposalFeesMutationVariables>(
    UPDATE_FEES_HUDDLEUP_MEMBER,
    {
      refetchQueries: [GET_PROPOSAL_QUERY, GET_VIEWER],
    },
  );

  const initializeSubscription = async () => {
    setIsLoading(true);
    await createHuddleUp({
      variables: {
        type: subscriptionType,
        stripeId: user?.companies[0]?.stripeCustomerId,
        userId: user?.id,
      },
      onCompleted(data) {
        setHuddleUpId(data.createHuddleUp.id);
        setOptions({ clientSecret: data.createHuddleUp.clientSecret, loader: 'always' });
      },
    });
    setStep(1);
    setIsLoading(false);
  };

  const confirmSubscription = async () => {
    setIsLoading(true);
    setLoadingText('Your membership is almost ready.');
    await updateHuddleUp({
      variables: {
        where: { id: huddleUpId },
        data: { status: { set: HuddleUpStatus.Active } },
      },
    });

    await updateFees({
      variables: {
        customerId: user.id,
      },
    });
    setStep(2);
    setLoadingText('');
    setIsLoading(false);
  };

  const listDetails = ['Lower Huddle Platform Fees.', 'Hire your first Huddler off-platform with zero Conversion Fee.'];
  const subDetails = [
    "We'll waive the platform fee when you work with one builder. Getting a Huddle team with 2 or more builders? HuddleUp cuts the platform fee in half.",
    `Want to work with a builder off the platform? We'll waive the conversion fee for your first Huddler hire and cut the fee in half for any future Huddler hire, whether it be for a full-time position or contract. Read more `,
  ];

  const listItems = listDetails.map(function (item, index) {
    return (
      <li key={index} className="mb-2 flex">
        <Check className="mr-2 text-sm p-0.5" />
        <div>
          <h1 className="text-[14px] font-medium pl-1 mb-1">{item}</h1>
          <h2 className="text-[12px] opacity-80 pl-1">
            {subDetails[index]}{' '}
            {index === 1 && (
              <a className="hover:underline" href="https://www.huddle.works/pricing">
                here.
              </a>
            )}
          </h2>
        </div>
      </li>
    );
  });

  return (
    <>
      <Modal ref={modalRef} maxHeight={false} needsPadding={false} closeOnBackdropClick={false}>
        {loading && <LoadingOverlay text={loadingText} />}
        {step === 0 && (
          <div className="w-[560px]">
            <div className="h-[108px] bg-[#FCE1B9]">
              <div className="items-center w-[560px] pb-0 flex justify-end bg-[#FCE1B9] p-6 ">
                <Close onClick={() => modalRef?.current?.close()} />
              </div>
              <div className="flex justify-center items-center w-[560px]  pb-0 absolute top-20">
                <div className="shadow-sm border-[5px] w-[93px] h-[93px] transform -rotate-12 flex justify-center items-center flex-shrink-0 shadow-[0px 14px 29px 0px bg-[#ffffff] border-[#fafafaca] rounded-[30px] z-[1] relative overflow-hidden">
                  <img
                    src={profilePic ? profilePic : '/images/BuilderProfilePic.png'}
                    className={`object-cover w-full ${profilePic ? 'h-full' : 'h-[120%]'}`}
                  />
                </div>

                <div className="-ml-6 shadow-md border-[5px] w-[93px] h-[93px] rotate-12 -mt-6 flex justify-center items-center flex-shrink-0 shadow-[0px 14px 29px 0px bg-[#EBB76A] border-[#FCE1B9] rounded-[30px]">
                  <img src={'/images/customer-photo-url.svg'} className="w-[48px] h-[48px]" />
                </div>
              </div>
            </div>
            <div className="h-[70px] bg-[#ffffff]">
              <div className="flex justify-center items-center w-[560px]  pb-0"></div>
            </div>
            <div className="bg-[#ffffff] px-8 p-8">
              <div className="">
                <div className="text-center text-[26px] font-dm-sans font-normal text-gray-900 leading-135 tracking-tighter ">
                  Join HuddleUp and save.
                </div>
              </div>
              <div>
                <div className=" bg-[#FAFAFA] px-4 py-4 my-5 rounded-xl ">
                  <h1 className="  px-2 py-4 font-bold text-[17px] leading-130 tracking-minus-0.17 text-grey-dark font-DMSans">
                    What you get:
                  </h1>
                  <ul className="">{listItems}</ul>
                </div>
              </div>

              <div className="justify-evenly flex ">
                <button
                  className="w-full relative"
                  onClick={() => setSubscriptionType(HuddleUpSubscriptionType.Quarterly)}
                >
                  <>
                    {subscriptionType === HuddleUpSubscriptionType.Quarterly ? (
                      <CheckCircle className="absolute top-0 -right-4" />
                    ) : (
                      <></>
                    )}
                    <div
                      className={`className={'block py-4 px-4  mx-2 border-2 rounded-lg '
                ${subscriptionType === HuddleUpSubscriptionType.Quarterly ? 'border-black' : ''}
                `}
                    >
                      <h1 className="text-huddleBlack text-xs not-italic font-bold uppercase">QUARTERLY</h1>
                      <p className="not-italic font-medium text-4xl flex text-center justify-center items-center">
                        <p className="not-italic font-medium text-sm">$</p>750
                      </p>
                      <p className="text-xs not-italic font-normal">per month</p>
                      <p className="text-xs not-italic font-normal">billed every 3 months</p>
                    </div>
                  </>
                </button>

                <button
                  className="w-full relative"
                  onClick={() => setSubscriptionType(HuddleUpSubscriptionType.Annual)}
                >
                  {subscriptionType === HuddleUpSubscriptionType.Annual ? (
                    <CheckCircle className="absolute top-0 -right-2" />
                  ) : (
                    <></>
                  )}
                  <div
                    className={`block py-4 px-4  mx-4 border-2 rounded-lg
                   ${subscriptionType === HuddleUpSubscriptionType.Annual ? 'border-black' : ''}
                  `}
                  >
                    <h1 className="text-huddleBlack text-xs not-italic font-bold uppercase">ANNUALY</h1>
                    <p className="not-italic font-medium text-4xl flex text-center justify-center items-center">
                      <p className="not-italic font-medium text-sm">$</p>500
                    </p>
                    <p className="text-xs not-italic font-normal">per month</p>
                    <p className="text-xs not-italic font-normal">billed once a year</p>
                  </div>
                </button>
              </div>

              <div className="pt-5 justify-center">
                <Button
                  size="xl"
                  color="dark"
                  className="w-full flex p-16px-28px justify-center items-center gap-10px self-stretch rounded-[16px]"
                  onClick={() => initializeSubscription()}
                >
                  <div className="font-dm-sans text-base font-normal leading-150 tracking-tighter text-[#EAE4F6]">
                    Join HuddleUP
                  </div>
                </Button>
              </div>
            </div>
          </div>
        )}
        {step === 1 && (
          <div className="p-8 w-[620px]">
            <div className="absolute top-4 left-4 cursor-pointer">
              <ArrowBack onClick={() => setStep(0)} />
            </div>
            <div className="absolute top-4 right-4 cursor-pointer">
              <Close onClick={() => modalRef?.current?.close()} />
            </div>
            <div className="text-gray-700 font-nn-nouvelle-grotesk text-[24px] leading-130 tracking-tighter px-8">
              Buy HuddleUp for 1 {subscriptionType === HuddleUpSubscriptionType.Quarterly ? 'Quarter' : 'Year'}
            </div>
            <div className="px-8 py-6">
              <Elements stripe={stripePromise} options={options}>
                <CheckoutForm onSuccess={confirmSubscription} setIsLoading={setIsLoading} />
              </Elements>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="justify-center px-16 py-16 flex flex-col items-center">
            <Tick className="w-[120px] h-[120px]" />
            <p className="pt-6 text-black font-nn-nouvelle-grotesk font-medium text-[36px] leading-120 tracking-tighter">
              Payment confirmed!
            </p>
            <Button
              onClick={() => modalRef.current.close()}
              size="xl"
              color="dark"
              className="w-full flex p-16px-28px justify-center items-center gap-10px self-stretch rounded-[16px] mt-2"
            >
              <p className="leading-150 font-normal tracking-tighter text-[#EAE4F6]">Welcome to HuddleUp!</p>
            </Button>
          </div>
        )}
      </Modal>
    </>
  );
};

export default HuddleUpPayment;

const CheckoutForm = ({ onSuccess, setIsLoading }: { onSuccess: () => void; setIsLoading?: (bool) => void }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async event => {
    setIsLoading(true);
    event.preventDefault();

    if (!stripe || !elements) return;

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: process.env.NEXT_PUBLIC_APP_URL },
      redirect: 'if_required',
    });

    if (error) {
      // This point will only be reached if there is an immediate error when confirming the payment. Show error to your customer (for example, payment incomplete)
      setErrorMessage(error.message);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="min-h-[325px]">
        <PaymentElement />
      </div>
      <Divider className="w-19/20 my-4" />
      <div className="items-center justify-center flex">
        <p className="text-black font-dm-sans font-bold text-xs leading-140 tracking-tighter">
          By selecting “Submit”, I agree to the <u>Terms of Use</u> and acknowledge that I have read the{' '}
          <u>Privacy Policy.</u>
        </p>
        <button
          className="w-[240px] h-[55px] place-items-center flex justify-center text-center rounded-lg flex-shrink-0 bg-black"
          disabled={!stripe}
        >
          <p className="text-white">Pay</p>
        </button>
      </div>
      <div className="py-2">{errorMessage && <div>{errorMessage}</div>}</div>
    </form>
  );
};
